import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Tooltip } from "@mui/material";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import {
  formatCalendarDay,
  temporalDayIn,
  type Temporal,
} from "@/utils/temporal";
import type { DocumentArrival } from "@/components/FileViewer/services/useDocumentMetadata";
import {
  keyForSource,
  useRecordDataSource,
} from "./Dashboard/recordDataSource";
import {
  fetchActivityRunMembers,
  ActivityLogRow,
  ActivityFeedPage,
} from "./Dashboard/api";
import InlineEntityCard from "./Dashboard/Cards/InlineEntityCard";
import { useDocumentConfig } from "./Data/documentConfig";
import { humanizeEnumKey } from "./Data/humanizeEnumKey";
import {
  activityDayHeading,
  activityTimeLabels,
  groupActivityByDay,
} from "./activityFeedTime";
import { entityKindMap } from "./utils";
import type {
  EntityKind,
  PolymorphicCellValue,
  ShallowEntity,
} from "./Dashboard/types";

interface ActivityFeedProps {
  // When set, scope the feed to a single entity via the
  // target_entity_public_id filter on /rms/activity-log/search.
  entityPublicId?: string;
  // Disable the underlying query (used by ActivityPanel to defer the
  // first fetch until the sliding panel actually opens).
  enabled?: boolean;
  // Re-key the query on demand. ActivityPanel bumps this each time the
  // panel reopens so the user sees fresh data; the entity tab leaves
  // it stable across renders.
  session?: number;
  // Which surface mounts the feed (#11426, design round 5). "panel": the
  // peek overlay — the feed owns its scroll, day headers stick, and more
  // pages load as you near the bottom. "tab": the entity page's Activity
  // card — the card is as tall as its content, the page scrolls, and more
  // history arrives through an explicit "Show more" doorway.
  variant?: "panel" | "tab";
  // Render nothing at all (no card, no state sentence) while the feed has
  // no rows — for a host that offers the feed as a bonus (the Overview's
  // reading column, round 8) rather than as the page's subject.
  hideWhenEmpty?: boolean;
  // When set (and `entityPublicId` is not), scope the feed to one document
  // via the target_document_id filter: the rows that target the document and
  // the rows whose source it is (#13433). The document is the page, so its
  // sentences never name it.
  documentId?: string;
  // How the document arrived, from its metadata. A document-scoped feed ends
  // with it, in its own day group, once the log has no more pages, so the
  // list is never empty.
  arrival?: DocumentArrival | null;
  // The folder the document sits in now. The arrival's "to <folder>" names
  // the folder before the oldest move the log holds, and this one only when
  // the log holds no move.
  arrivalFolder?: string[] | null;
}

const PAGE_SIZE = 50;

// A row the feed can actually place: one whose Time cell stated a moment. The
// feed is a chronology, so a row without one has nowhere to go — it is dropped
// alongside the contentless ones rather than filed under a day invented for it.
type DatedActivityLogRow = ActivityLogRow & { occurred_at: Temporal };

const hasOccurrence = (row: ActivityLogRow): row is DatedActivityLogRow =>
  row.occurred_at !== null;

const isPath = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((part) => typeof part === "string");

// The folder a document sat in before the oldest move among `rows` (newest
// first): the `filesystem_path` an `update-document` row recorded before it
// moved the document. Null when no row moved it.
const folderBeforeFirstMove = (
  rows: readonly ActivityLogRow[]
): string[] | null => {
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    if (row.verb !== "update-document") continue;
    const before = detailsObject(row.details_before)?.filesystem_path;
    const after = detailsObject(row.details_after)?.filesystem_path;
    if (isPath(before) && isPath(after)) return before;
  }
  return null;
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  entityPublicId,
  enabled = true,
  session = 0,
  variant = "panel",
  hideWhenEmpty = false,
  documentId: documentIdProp,
  arrival = null,
  arrivalFolder = null,
}) => {
  // A record scope wins: the two never combine.
  const documentId = entityPublicId ? undefined : documentIdProp;
  const filterParam = useMemo(() => {
    if (entityPublicId) {
      return JSON.stringify([
        { name: "target_entity_public_id", op: "eq", value: entityPublicId },
      ]);
    }
    if (documentId) {
      return JSON.stringify([
        { name: "target_document_id", op: "eq", value: documentId },
      ]);
    }
    return undefined;
  }, [entityPublicId, documentId]);

  // Read through the record's data source (#13370, round 36c): Appearance's demo record answers
  // from its own rows.
  const source = useRecordDataSource();
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading, error } =
    useInfiniteQuery({
      queryKey: keyForSource(source, [
        "rms-activity-feed",
        entityPublicId ?? null,
        documentId ?? null,
        session,
      ]),
      enabled,
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: ActivityFeedPage) => {
        // The page says whether the log continues (#11876): a page folds
        // runs, so it can be short while older rows remain, and it can only
        // be the last page when the backend says so. Without more, the tab
        // renders no "Show earlier activity" doorway whose click would fetch
        // nothing (round-6 review).
        if (!last.hasMore) return undefined;
        // A page with no rows still says where to resume: every row the
        // backend read was hidden, and the log goes on below them.
        return (
          last.rows[last.rows.length - 1]?.keyset_id ??
          last.nextKeysetId ??
          undefined
        );
      },
      queryFn: ({ pageParam }) =>
        source.activity({
          page_size: PAGE_SIZE,
          keyset_id: pageParam,
          filter: filterParam,
          enhance_display: true,
        }),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 30_000,
    });

  const viewerZone = useViewerTimeZone();
  const rows: DatedActivityLogRow[] = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.rows) ?? [])
        .filter(hasOccurrence)
        .filter((r) => !isFieldClear(r) && !isContentlessUpdate(r)),
    [data]
  );
  // The arrival closes a document's history once the log has nothing older
  // to page in (#13433). It is composed here rather than synthesized in the
  // keyset-paged search: the blob is not an audit row.
  const arrivalShown =
    Boolean(documentId) &&
    arrival !== null &&
    data !== undefined &&
    !hasNextPage &&
    !error;
  // The arrival shows only once every page is in, so the oldest move in the
  // loaded rows is the document's first: its starting folder is where the
  // document arrived.
  const arrivalDestination = useMemo(
    () => folderBeforeFirstMove(rows) ?? arrivalFolder,
    [rows, arrivalFolder]
  );
  const clustered = useMemo(
    () => attachInteractionSibling(clusterAiBatches(rows)),
    [rows]
  );
  const dayGroups = useMemo(
    () =>
      groupActivityByDay(
        clustered,
        (entry) => entry.leader.occurred_at,
        viewerZone
      ).map((group) => ({
        ...group,
        // Identical actor+verb+target+minute rows collapse into one counted
        // row (#11426, design round 6): five "registered Cordillera…" lines
        // were the first thing the eye hit in the panel. Within the day
        // group, so a run can never straddle a date header.
        entries: collapseRepeats(
          group.entries,
          viewerZone,
          Boolean(documentId)
        ),
      })),
    [clustered, viewerZone, documentId]
  );

  const isPanel = variant === "panel";

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Panel only: the tab's root is not a scroll container, so the sensor's
    // near-bottom test is unconditionally true there and it would drain the
    // entire history page by page (round-6 review B3). The tab paginates
    // through its explicit doorway instead.
    const root = scrollContainerRef.current;
    if (!root || !hasNextPage || !isPanel) return;
    const onScroll = () => {
      if (isFetching) return;
      const nearBottom =
        root.scrollTop + root.clientHeight >= root.scrollHeight - 200;
      if (nearBottom) fetchNextPage();
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => root.removeEventListener("scroll", onScroll);
  }, [hasNextPage, isFetching, fetchNextPage, isPanel]);

  // Both variants: a page that shows nothing while the log continues (every
  // row it read was hidden) is followed at once, until something shows or
  // the log ends. The tab's doorway and the panel's sensor both need a row
  // on screen to work from; this is the one case neither has.
  useEffect(() => {
    // The panel's sensor may ask in the same commit; a fetch already in
    // flight is joined, not restarted.
    if (rows.length === 0 && hasNextPage && !isFetching) {
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [rows.length, hasNextPage, isFetching, fetchNextPage]);

  const stateText =
    (isLoading || hasNextPage) && rows.length === 0
      ? "Loading activity…"
      : error && rows.length === 0
        ? "Activity could not be loaded."
        : rows.length === 0 && !arrivalShown
          ? entityPublicId
            ? "No activity recorded for this record."
            : "No activity recorded yet."
          : null;

  // A bonus surface (the Overview) renders nothing while there is nothing —
  // no card, no loading beat, no empty sentence (round 8).
  if (hideWhenEmpty && stateText) return null;

  // The arrival, once shown, files under its own day: the oldest row's day
  // group when they share one (a heading never repeats), else a group of its
  // own after it.
  const arrivalDay =
    arrivalShown && arrival ? temporalDayIn(arrival.at, viewerZone) : null;
  const arrivalRow =
    arrivalShown && arrival ? (
      <ArrivalRow
        arrival={arrival}
        folder={arrivalDestination}
        viewerZone={viewerZone}
      />
    ) : null;
  const arrivalJoinsLastDay =
    arrivalDay !== null &&
    dayGroups.length > 0 &&
    dayGroups[dayGroups.length - 1].day === arrivalDay;

  const body = stateText ? null : (
    <>
      {dayGroups.map((group, groupIndex) => (
        <div
          key={group.day}
          style={{
            borderTop:
              groupIndex === 0 ? undefined : "1px solid var(--fp-border)",
            marginTop: groupIndex === 0 ? 0 : 4,
          }}
        >
          {/* The day heading owns the date at overline scale; the rows
              own the clock. In the panel it sticks — with this much
              scroll the reader loses the date otherwise. */}
          <DayHeading
            day={group.day}
            viewerZone={viewerZone}
            first={groupIndex === 0}
            sticky={isPanel}
          />
          {/* A contiguous same-actor same-verb stretch over several objects
              arrives from the backend already folded into a run (#11876):
              the feed never forms one itself, so a run's count is the
              server's count over the whole run and cannot change as later
              pages land. An entity page's rows all target the page itself,
              so no run forms there and the sentence rows stand. */}
          {group.entries.map((entry, idx) =>
            entry.kind === "run" ? (
              <RunRows
                key={`run-${idx}-${entry.leader.entity_id}`}
                leader={entry.leader}
                members={entry.members}
                eventCount={entry.eventCount}
                objectCount={entry.objectCount}
                filterParam={filterParam}
                viewerZone={viewerZone}
              />
            ) : (
              <EntryRow
                key={`${entry.kind}-${idx}-${entry.leader.entity_id}`}
                entry={entry}
                viewerZone={viewerZone}
                hideTargetChip={Boolean(entityPublicId)}
                // A document's history sits in the 320px Details slot, where
                // one truncated line shows the actor and little else: its
                // sentences wrap, as the tab's do.
                oneLine={isPanel && entry.kind !== "group" && !documentId}
                documentId={documentId}
              />
            )
          )}
          {arrivalJoinsLastDay &&
            groupIndex === dayGroups.length - 1 &&
            arrivalRow}
        </div>
      ))}

      {arrivalDay !== null && !arrivalJoinsLastDay && (
        // A day group of its own, last: the arrival is where the history
        // starts. On the day of the oldest row it joins that day instead.
        <div
          style={{
            borderTop:
              dayGroups.length === 0 ? undefined : "1px solid var(--fp-border)",
            marginTop: dayGroups.length === 0 ? 0 : 4,
          }}
        >
          <DayHeading
            day={arrivalDay}
            viewerZone={viewerZone}
            first={dayGroups.length === 0}
            sticky={isPanel}
          />
          {arrivalRow}
        </div>
      )}

      {hasNextPage &&
        (isPanel ? (
          // The panel loads as you near the bottom; this line only says
          // that more is on the way.
          isFetching && (
            <div
              className="text-center"
              style={{
                padding: "12px 0 4px",
                fontSize: "var(--fp-t-meta)",
                color: "var(--fp-ink-4)",
              }}
            >
              Loading more…
            </div>
          )
        ) : (
          // The tab's card is as tall as its content and the PAGE owns
          // the scroll, so more history arrives through an explicit
          // doorway rather than a scroll sensor watching the wrong
          // container.
          <div
            style={{
              borderTop: "1px solid var(--fp-border)",
              marginTop: 4,
              paddingTop: 10,
            }}
          >
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetching}
              className="cursor-pointer disabled:opacity-50 hover:underline underline-offset-2"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                fontSize: "var(--fp-t-meta)",
                fontWeight: 500,
                color: "var(--fp-ink-3)",
              }}
            >
              {isFetching ? "Loading…" : "Show earlier activity"}
            </button>
          </div>
        ))}
    </>
  );

  if (isPanel) {
    return (
      <div
        ref={scrollContainerRef}
        className="h-full overflow-auto"
        style={{
          fontFamily: "var(--fp-font-ui)",
          fontFeatureSettings: "var(--fp-feat-ui)",
          background: "var(--fp-surface)",
        }}
      >
        {stateText ? (
          stateSentence(stateText)
        ) : (
          <div style={{ padding: "6px 22px 26px" }}>{body}</div>
        )}
      </div>
    );
  }

  // The tab variant owns its card chrome, because the chrome follows the
  // feed's state (#11426, design round 6): a populated log is a card on the
  // page ground, held at a ~3-row minimum so a one-event log still reads as
  // a card; the empty and loading states sit on the ground itself — an
  // empty card is a box drawn around nothing.
  return (
    <div
      style={{
        fontFamily: "var(--fp-font-ui)",
        fontFeatureSettings: "var(--fp-feat-ui)",
      }}
    >
      {stateText ? (
        stateSentence(stateText)
      ) : (
        <div
          data-activity-card
          style={{
            background: "var(--fp-surface)",
            border: "1px solid var(--fp-border)",
            borderRadius: "var(--fp-card-r)",
            boxShadow: "var(--fp-lift)",
            padding: "var(--fp-card-pad)",
            minHeight: 150,
          }}
        >
          <div
            className="flex items-baseline"
            style={{ gap: "var(--fp-gap-tight)", marginBottom: 4 }}
          >
            <h2
              style={{
                fontSize: "var(--fp-t-lede)",
                fontWeight: "var(--fp-w-head)" as unknown as number,
                letterSpacing: "var(--fp-track-head)",
                color: "var(--fp-ink)",
                margin: 0,
              }}
            >
              Activity
            </h2>
            <span
              style={{
                fontSize: "var(--fp-t-meta)",
                color: "var(--fp-ink-4)",
              }}
            >
              newest first
            </span>
          </div>
          {body}
        </div>
      )}
    </div>
  );
};

const DayHeading: React.FC<{
  day: string;
  viewerZone: string;
  first: boolean;
  sticky: boolean;
}> = ({ day, viewerZone, first, sticky }) => (
  <div
    data-activity-day={day}
    className={sticky ? "sticky top-0 z-10" : undefined}
    style={{
      padding: first ? "2px 0 4px" : "12px 0 4px",
      background: sticky ? "var(--fp-surface)" : undefined,
      fontSize: "var(--fp-t-micro)",
      fontWeight: "var(--fp-w-head)" as unknown as number,
      letterSpacing: "var(--fp-track-caps)",
      textTransform: "uppercase",
      color: "var(--fp-ink-4)",
    }}
  >
    {activityDayHeading(day, viewerZone)}
  </div>
);

// The clock column every feed row ends with: the clock at the data level,
// tabular so a column of times aligns, the full moment one hover away.
const ClockColumn: React.FC<{ labels: { clock: string; full: string } }> = ({
  labels,
}) => (
  <Tooltip title={labels.full} arrow>
    <span
      className="flex-none whitespace-nowrap text-right"
      style={{
        width: 64,
        marginLeft: "auto",
        paddingLeft: 8,
        fontSize: "var(--fp-t-fine)",
        color: "var(--fp-ink-4)",
        fontFamily: "var(--fp-font-num)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {labels.clock}
    </span>
  </Tooltip>
);

// The last row of a document's history (#13433): how it arrived. The actor
// leads, as on every row, except for an emailed document, whose sender is
// an address rather than someone acting in the product.
const ArrivalRow: React.FC<{
  arrival: DocumentArrival;
  folder: string[] | null;
  viewerZone: string;
}> = ({ arrival, folder, viewerZone }) => {
  const labels = activityTimeLabels(arrival.at, viewerZone);
  const actor = (
    <span className="font-medium" style={{ color: "var(--fp-ink)" }}>
      {arrival.actor}
    </span>
  );
  const destination =
    folder && folder.length > 0 ? (
      <>
        {" to "}
        <span className="font-medium">{folder.join(" / ")}</span>
      </>
    ) : null;
  let sentence: React.ReactNode;
  switch (arrival.kind) {
    case "upload":
      sentence = (
        <>
          {actor} uploaded{destination}
        </>
      );
      break;
    case "email":
      sentence = <>Email received from {actor}</>;
      break;
    case "system":
      // A connector's sync names the connector; any other system action
      // reads as "System imported".
      sentence = (
        <>
          {actor} {arrival.connector ? "sync imported" : "imported"}
          {destination}
        </>
      );
      break;
    default: {
      const unknownKind: never = arrival.kind;
      throw new Error(`Unknown arrival kind: ${String(unknownKind)}`);
    }
  }
  return (
    <div
      data-activity-row
      data-activity-arrival
      style={{
        padding: "7px 0",
        fontSize: "var(--fp-t-body)",
        color: "var(--fp-ink-2)",
        lineHeight: 1.55,
      }}
    >
      <div className="flex items-baseline min-w-0">
        <span className="min-w-0">{sentence}</span>
        <ClockColumn labels={labels} />
      </div>
    </div>
  );
};

// One quiet sentence for every non-data state (#11426, design round 5):
// no illustration, no spinner theatre.
const stateSentence = (text: string) => (
  <div
    className="text-center"
    style={{
      padding: "44px 18px",
      fontSize: "var(--fp-t-body)",
      color: "var(--fp-ink-4)",
    }}
  >
    {text}
  </div>
);

// ─── Row renderer ────────────────────────────────────────────────

type ClusteredEntry =
  | { kind: "row"; leader: DatedActivityLogRow }
  | {
      kind: "group";
      leader: DatedActivityLogRow;
      members: DatedActivityLogRow[];
      fromInteraction?: PolymorphicCellValue;
    }
  // A stretch of one actor doing one thing to one object, collapsed to a
  // counted row (design round 6; widened by the Aug-31 CD ruling: the
  // same-object case collapses across minutes and details, because
  // "updated · 3 interactions" over one interaction three times is the
  // panel stating something false). `members` are the occurrences beyond
  // the leader; `mixedDetails` records that the occurrences changed
  // different things, so the sentence must generalize to verb + object
  // rather than let one occurrence's detail speak for the others.
  | {
      kind: "repeat";
      leader: DatedActivityLogRow;
      members: DatedActivityLogRow[];
      mixedDetails: boolean;
    }
  // A same-actor same-verb run over several objects, folded by the backend
  // (#11876). `members` are the rows shipped after the leader — enough for
  // the visible cap, not necessarily the whole run — and the counts cover
  // the whole run: events for "Show N more", distinct objects for the
  // header. The rest of the run is fetched on demand by its head and tail
  // keysets.
  | {
      kind: "run";
      leader: DatedActivityLogRow;
      members: DatedActivityLogRow[];
      eventCount: number;
      objectCount: number;
    };

// Sentence rows: everything the run grammar does not own.
type SentenceEntry = Exclude<ClusteredEntry, { kind: "run" }>;

// Two rows read identically when their sentence inputs match: the actor,
// the verb, the target, the source, the minute the clock column shows, and
// whatever slice of details the verb's summary actually renders. Hashing
// the WHOLE details payload would be wrong in both directions: create rows
// carry payloads the sentence never shows (five identical "registered X"
// lines would survive), while two different fact edits at the same minute
// must not collapse into one mislabelled row — so the key is exactly what
// each verb branch prints.
//
// `documentScope` is a document's own history (#13433), whose sentences print
// what each document edit changed; everywhere else an update-document row
// prints no detail, and folds as it always has.
function sentenceDetailKey(
  row: DatedActivityLogRow,
  documentScope: boolean
): unknown {
  switch (row.verb) {
    case "update-fact":
      return extractFactChanges(row);
    case "link-documents":
      return countBlobIds(row);
    case "unlink-documents":
      return countUnlinkBlobIds(row);
    case "move-document": {
      const d = row.details_after as Record<string, unknown> | null;
      return d && typeof d === "object"
        ? (d.document_types ?? d.document_type ?? null)
        : null;
    }
    // A document's own history prints what each edit changed (#13433), so
    // there two edits in one minute read alike only when they changed the
    // same.
    case "update-document":
      return documentScope
        ? [row.details_before, row.details_after, row.linked, row.unlinked]
        : null;
    case "set-manager":
      return extractManagerDisplay(row);
    case "update-interaction":
      return extractChangedFields(row);
    case "link-document":
      return extractFilename(row);
    case "rename-entity":
      return extractRenamedName(row);
    default:
      return null;
  }
}

// Verbs whose sentenceDetailKey never carries the sentence's subject —
// changed-field lists and ignored payloads — so occurrences may differ in
// detail and still honestly collapse under one generalized sentence.
const RELAXED_COLLAPSE_VERBS = new Set([
  "create-entity",
  "create-interaction",
  "update-entity",
  "update-interaction",
]);

function repeatSignature(
  entry: ClusteredEntry,
  zone: string,
  documentScope: boolean
): string | null {
  if (entry.kind !== "row") return null;
  const r = entry.leader;
  const actor = r.is_finpilot_ai
    ? "ai"
    : (r.user_id ?? r.actor_display ?? "system");
  // A stretch over ONE object collapses however far apart its moments and
  // details are (#11426 Aug-31, CD ruling): three edits to one record are
  // one row saying "· 3 times", never a "run" whose count claims three
  // objects. Reasoning rows fold here too — each occurrence carries its
  // own Why in the disclosed list. Only verbs whose detail is ELABORATION
  // are eligible (review finding): for link-document the detail is the
  // filename and for update-fact the field and value — the sentence's
  // actual subject — so collapsing those would destroy information with
  // no disclosure path back to it. They keep the strict same-detail rule.
  if (r.target?.id && RELAXED_COLLAPSE_VERBS.has(r.verb ?? "")) {
    return JSON.stringify([
      "object",
      actor,
      r.verb,
      r.target.kind,
      r.target.id,
      r.source?.id ?? null,
      r.bulk_target_count ?? null,
    ]);
  }
  // A targetless row keeps the strict identity — same minute, same
  // rendered detail — and a reasoning row never folds on this path: with
  // no object to anchor them, two reasonings under one sentence would be
  // indistinguishable (round-6 review).
  if (r.llm_reasoning) return null;
  return JSON.stringify([
    "exact",
    actor,
    r.verb,
    r.source?.id ?? null,
    r.bulk_target_count ?? null,
    activityTimeLabels(r.occurred_at, zone).clock,
    sentenceDetailKey(r, documentScope),
  ]);
}

function collapseRepeats(
  entries: ClusteredEntry[],
  zone: string,
  documentScope: boolean
): ClusteredEntry[] {
  const out: ClusteredEntry[] = [];
  let i = 0;
  while (i < entries.length) {
    const first = entries[i];
    const sig = repeatSignature(first, zone, documentScope);
    if (sig === null) {
      out.push(first);
      i += 1;
      continue;
    }
    const members: DatedActivityLogRow[] = [];
    let j = i + 1;
    while (
      j < entries.length &&
      repeatSignature(entries[j], zone, documentScope) === sig
    ) {
      members.push(entries[j].leader);
      j += 1;
    }
    if (members.length === 0) {
      out.push(first);
    } else {
      const detailKeys = new Set(
        [first.leader, ...members].map((r) =>
          JSON.stringify(sentenceDetailKey(r, documentScope) ?? null)
        )
      );
      out.push({
        kind: "repeat",
        leader: first.leader,
        members,
        mixedDetails: detailKeys.size > 1,
      });
    }
    i = j;
  }
  return out;
}

// ─── Runs (#11426 Aug-30 round, CD ruling; folded server-side since #11876) ──
//
// A contiguous stretch of same-actor same-verb rows inside one day is ONE
// event repeated over objects, and the panel used to spell the constant
// part thirty times in bold while the one varying field — the object —
// wore a wrapping chip. The run inverts that: the actor+verb demotes to a
// small run header stated once, with its count, and each OBJECT becomes a
// row title — a single-line truncating link with its own time in the fixed
// right column. Long runs cap at eight objects behind a "Show N more" row
// so one bulk import cannot push every other day below the fold.
//
// Which rows form a run — same actor, same verb, a target, no source the
// sentence would have to state, at least three events over more than one
// object, inside one of the viewer's calendar days — is the backend's rule
// now (#11876): a run is a unit of pagination, so it has to be folded where
// the pages are cut. Folding it here over whatever pages had arrived made
// the count grow page by page, and because the fold adds no scroll height
// the near-bottom sensor kept firing until the log was drained.

const RUN_VISIBLE_CAP = 8;

// What the run's count counts, in the objects' own word: "14 documents",
// "31 interactions", "9 records" — "items" only for a genuinely mixed run.
// The kind comes from the backend over the WHOLE run (#11876): the members
// on the row are a sample, and a noun read off a sample could flip when the
// rest arrives.
function runObjectNoun(kind: string | null, count: number): string {
  const singular =
    kind === "document"
      ? "document"
      : kind === "interaction"
        ? "interaction"
        : kind && kind in entityKindMap
          ? "record"
          : "item";
  return count === 1 ? singular : `${singular}s`;
}

// One object inside a run: a single-line truncating link with its own
// time — no glyph (the header owns the verb, so a mark under it is
// decoration — CD Aug-31) and no title tooltip (real-7/12: ordinary
// truncated text carries none; the object is navigable and its full name
// is at its destination). Where the row carries reasoning it carries its
// own quiet Why, disclosed beneath this row and independent of its
// neighbours (no expand-all, no auto-open — CD).
const RunObjectRow: React.FC<{
  row: DatedActivityLogRow;
  viewerZone: string;
  // Disclosure is owned by the run so opening one row's Why closes the
  // previously open one (CD: the panel never holds two reasoning walls
  // at once — a run of 15 with three open is a scroll problem the user
  // unwinds by hand).
  open: boolean;
  onToggle: () => void;
}> = ({ row, viewerZone, open, onToggle }) => {
  const occurred = activityTimeLabels(row.occurred_at, viewerZone);
  const actorName = row.is_finpilot_ai
    ? "Finpilot AI"
    : (row.actor_display ?? row.user_id ?? "System");
  return (
    <div data-run-object>
      <div
        className="group flex items-baseline gap-3"
        style={{
          padding: "3px 0",
          fontSize: "var(--fp-t-body)",
          color: "var(--fp-ink)",
          lineHeight: 1.5,
        }}
      >
        <span className="min-w-0 flex-1 truncate">
          <SidebarChip value={row.target} hideGlyph />
        </span>
        {row.llm_reasoning && (
          <WhyButton
            open={open}
            actorName={actorName}
            onToggle={onToggle}
            revealOnRowHover
          />
        )}
        <Tooltip title={occurred.full} arrow>
          <span
            className="flex-none whitespace-nowrap text-right"
            style={{
              width: 64,
              fontSize: "var(--fp-t-fine)",
              color: "var(--fp-ink-4)",
              fontFamily: "var(--fp-font-num)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {occurred.clock}
          </span>
        </Tooltip>
      </div>
      {row.llm_reasoning && open && (
        <ReasoningBlock>{row.llm_reasoning}</ReasoningBlock>
      )}
    </div>
  );
};

const RunRows: React.FC<{
  leader: DatedActivityLogRow;
  // The members shipped with the leader, in feed order. The backend sends
  // enough for the visible cap; a longer run's remainder is fetched when
  // the reader asks for it.
  members: DatedActivityLogRow[];
  // Backend counts over the WHOLE run (#11876). The header's number
  // counts OBJECTS, not events (CD Aug-31): an object can recur inside a
  // run (A, B, A), so the two differ — the rows stay events, each with its
  // own time, and the header never claims more things than were touched.
  eventCount: number;
  objectCount: number;
  filterParam: string | undefined;
  viewerZone: string;
}> = ({
  leader,
  members,
  eventCount,
  objectCount,
  filterParam,
  viewerZone,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [fetched, setFetched] = useState<DatedActivityLogRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [openWhyRow, setOpenWhyRow] = useState<string | null>(null);
  const rows = useMemo(
    () => [leader, ...(fetched ?? members)],
    [leader, fetched, members]
  );
  const visible = expanded ? rows : rows.slice(0, RUN_VISIBLE_CAP);
  // Hidden counts against the run's true size, not against what happens
  // to be on the row: a run of 237 with seven shipped members hides 229.
  // Once the rest has been fetched, what came back IS the run — a member
  // gone from the log since the page loaded must not leave a doorway that
  // re-asks for the same rows on every click.
  const total = fetched !== null ? rows.length : eventCount;
  const hidden = Math.max(0, total - visible.length);
  const showMore = async () => {
    // The run's head is the leader's own keyset; its tail is the row's
    // pagination cursor, which the backend set to the run's oldest member.
    if (
      fetched !== null ||
      rows.length >= eventCount ||
      !leader.run_head_keyset_id
    ) {
      setExpanded(true);
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      const rest = await fetchActivityRunMembers({
        head_keyset_id: leader.run_head_keyset_id,
        tail_keyset_id: leader.keyset_id,
        filter: filterParam,
      });
      setFetched(rest.filter(hasOccurrence));
      setExpanded(true);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div data-activity-run style={{ padding: "7px 0" }}>
      {/* The run header: the actor+verb stated once at label weight, and
          the run's size as a fact — never a headline, and never a
          reasoning summary (CD Aug-31: disclosure state lives on the
          rows). No glyph: the feed's rows start at one left edge, with
          day and run headers as the only structure. */}
      <div
        style={{
          fontSize: "var(--fp-t-meta)",
          color: "var(--fp-ink-3)",
          lineHeight: 1.55,
        }}
      >
        <span data-run-head className="block min-w-0 truncate">
          <ActorBadge row={leader} /> {formatVerb(leader.verb)}
          {" · "}
          <span
            style={{
              fontFamily: "var(--fp-font-num)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {objectCount}
          </span>{" "}
          {runObjectNoun(leader.run_object_kind, objectCount)}
        </span>
      </div>
      {/* Object rows: a shallow indent under the header — the run's own
          structure — each a single-line truncating link with its own time:
          one clean left edge, one clean right edge, no wrap. */}
      <div style={{ paddingLeft: 14 }}>
        {visible.map((row) => (
          <RunObjectRow
            key={row.keyset_id}
            row={row}
            viewerZone={viewerZone}
            open={openWhyRow === row.keyset_id}
            onToggle={() =>
              setOpenWhyRow((current) =>
                current === row.keyset_id ? null : row.keyset_id
              )
            }
          />
        ))}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => void showMore()}
            disabled={loading}
            className="cursor-pointer disabled:opacity-50 hover:underline underline-offset-2"
            style={{
              background: "none",
              border: "none",
              padding: "3px 0",
              fontSize: "var(--fp-t-meta)",
              color: "var(--fp-ink-3)",
            }}
          >
            {loading ? "Loading…" : `Show ${hidden} more`}
          </button>
        )}
        {failed && !loading && (
          <div
            role="alert"
            style={{
              padding: "3px 0",
              fontSize: "var(--fp-t-meta)",
              color: "var(--fp-ink-3)",
            }}
          >
            The rest could not be loaded.
          </div>
        )}
        {expanded && rows.length > RUN_VISIBLE_CAP && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="cursor-pointer hover:underline underline-offset-2"
            style={{
              background: "none",
              border: "none",
              padding: "3px 0",
              fontSize: "var(--fp-t-meta)",
              color: "var(--fp-ink-3)",
            }}
          >
            Show fewer
          </button>
        )}
      </div>
    </div>
  );
};

// The one Why control (#11426 Aug-31, CD ruling): the word stays "Why"
// in both states — a swapped label flickers the control's identity — with
// open marked by ink and a persistent underline, never a second glyph.
// The visible word opens the accessible name (WCAG 2.5.3 — "click Why"
// must match) and the name never flips; aria-expanded carries the state.
// One component for the sentence row, the run object row and the folded
// occurrence, so the three cannot drift.
const WhyButton: React.FC<{
  open: boolean;
  actorName: string;
  onToggle: () => void;
  // Inside runs and occurrence lists the word reveals on the ROW's hover
  // and on focus (CD Aug-31, second ruling — "the defect is mine"): a run
  // is by construction a stretch of reasoning'd rows, so within it the
  // word's presence carries zero information per row, and a rail of
  // identical "Why"s is rule-31 texture wearing a word. Opacity, never
  // display — the slot stays reserved so a reveal can't shift the object
  // or the time column — and `[@media(hover:none)]` keeps it painted on
  // coarse pointers, where there is no hover to reveal with and the
  // object link already owns the tap. Singles keep the always-visible
  // word: there, presence is a fact about the row.
  revealOnRowHover?: boolean;
}> = ({ open, actorName, onToggle, revealOnRowHover = false }) => (
  <button
    type="button"
    aria-expanded={open}
    aria-label={`Why — ${actorName}'s reasoning`}
    onClick={onToggle}
    className={`flex-none inline-flex items-center ml-1.5 align-baseline rounded px-1 py-0.5 cursor-pointer focus-visible:outline focus-visible:outline-1 focus-visible:outline-gray-400 ${
      open
        ? "underline text-[var(--fp-ink-2)]"
        : "text-[var(--fp-ink-4)] hover:underline hover:text-[var(--fp-ink-2)]"
    } ${
      revealOnRowHover && !open
        ? "opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
        : ""
    }`}
    style={{ fontSize: "var(--fp-t-meta)" }}
  >
    Why
  </button>
);

// The disclosed account (rule 18): selectable, wrapping at reading
// measure and never truncating, the left hairline carrying the
// attribution — the author is by construction the row's actor, named in
// the sentence, so a name prefix would paint a field that can never vary
// (CD Aug-31). It sits ONE STEP DOWN at the meta size (keeper decision,
// seconded by CD: the hold-the-size condition was contingent on the step
// being small, and a 15-line reasoning wall at sentence size made the
// subordinate material the loudest thing on screen) with PROSE
// line-height — meta at the label leading would be a slab.
const ReasoningBlock: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    data-reasoning
    className="mt-1.5 mb-1 pl-3 max-w-[65ch]"
    style={{
      borderLeft: "2px solid var(--fp-border)",
      color: "var(--fp-ink-3)",
      fontSize: "var(--fp-t-meta)",
      lineHeight: 1.5,
    }}
  >
    {children}
  </div>
);

// A folded moment inside a collapsed row: its clock at full precision,
// and — where THAT occurrence carries reasoning — its own Why (CD Aug-31:
// the collapsed row holds several events and therefore possibly several
// reasonings, so there is no single "the" reasoning to hang on the row).
const OccurrenceRow: React.FC<{
  occ: DatedActivityLogRow;
  viewerZone: string;
  open: boolean;
  onToggle: () => void;
}> = ({ occ, viewerZone, open, onToggle }) => {
  const actorName = occ.is_finpilot_ai
    ? "Finpilot AI"
    : (occ.actor_display ?? occ.user_id ?? "System");
  return (
    <div data-repeat-occurrence className="group" style={{ padding: "1px 0" }}>
      <span
        style={{
          fontSize: "var(--fp-t-fine)",
          color: "var(--fp-ink-3)",
          fontFamily: "var(--fp-font-num)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {activityTimeLabels(occ.occurred_at, viewerZone).full}
      </span>
      {occ.llm_reasoning && (
        <WhyButton
          open={open}
          actorName={actorName}
          onToggle={onToggle}
          revealOnRowHover
        />
      )}
      {occ.llm_reasoning && open && (
        <ReasoningBlock>{occ.llm_reasoning}</ReasoningBlock>
      )}
    </div>
  );
};

const EntryRow: React.FC<{
  entry: SentenceEntry;
  viewerZone: string;
  hideTargetChip?: boolean;
  // The panel's rows hold ONE line (CD Aug-31): the sentence never wraps
  // and the trailing object truncates, so singles and run object rows
  // share a grammar and the row rhythm stops depending on title length.
  // The tab's short, targetless sentences keep their wrap, as do grouped
  // multi-object sentences whose tail ("and 3 others") must stay legible.
  oneLine?: boolean;
  // A document's own history (#13433): sentences never name the document.
  documentId?: string;
}> = ({
  entry,
  viewerZone,
  hideTargetChip = false,
  oneLine = false,
  documentId,
}) => {
  const leader = entry.leader;
  const actorNode = <ActorBadge row={leader} />;
  const occurred = activityTimeLabels(leader.occurred_at, viewerZone);

  // The AI's reasoning is disclosure, not hover (#11426, rule 32 and the
  // keeper's disclosure ruling): prose in a tooltip can't be selected,
  // quoted, or finished, and the reasoning is the only thing that makes a
  // machine-made change reviewable. The affordance is the word "Why" in
  // the feed's meta type after the sentence, ALWAYS legible on rows that
  // carry reasoning (CD Aug-31, replacing the hover-revealed caret the
  // keeper rejected: an affordance you must first hover to discover is a
  // secret, and the rotating glyph taught a mechanism instead of naming
  // what's inside). Rule 31 bans per-row glyph TEXTURE — a column of
  // identical marks read as pattern — and a word is read, not scanned;
  // since only AI rows carry it, its presence states a fact about the
  // row. Wrapping the sentence itself in a button role was tried first
  // and strips the sentence's links for assistive tech (see the inline
  // comment at the button). Rows without reasoning get no disclosure at
  // all (rule 3).
  const reasoning = entry.kind === "row" ? leader.llm_reasoning : null;
  const [reasoningOpen, setReasoningOpen] = useState(false);
  // A collapsed run's count is a disclosure to the individual occurrences.
  const [occurrencesOpen, setOccurrencesOpen] = useState(false);
  const [openOccurrenceWhy, setOpenOccurrenceWhy] = useState<string | null>(
    null
  );
  const repeatTotal = entry.kind === "repeat" ? entry.members.length + 1 : 0;
  const actorName = leader.is_finpilot_ai
    ? "Finpilot AI"
    : (leader.actor_display ?? leader.user_id ?? "System");

  const sentence = (
    <>
      {actorNode}{" "}
      {entry.kind === "group" ? (
        <GroupedSummary
          leader={leader}
          members={entry.members}
          fromInteraction={entry.fromInteraction}
          hideTargetChip={hideTargetChip}
          hideFromDocumentId={documentId}
        />
      ) : documentId && !(entry.kind === "repeat" && entry.mixedDetails) ? (
        <DocumentRowSummary row={leader} documentId={documentId} />
      ) : entry.kind === "repeat" && entry.mixedDetails ? (
        // The occurrences changed different things, so any one
        // occurrence's detail fragment would misstate the others: the
        // collapsed sentence generalizes to verb + object (CD Aug-31).
        // On a record's own tab the chip is hidden, but a bare verb still
        // needs an object (round 7) — "this <kind>" says it without
        // repeating the page's name.
        <span>
          {formatVerb(leader.verb)}
          {hideTargetChip && leader.target && (
            <>
              {" "}
              this{" "}
              {entityKindMap[
                leader.target.kind as EntityKind
              ]?.label.toLowerCase() ?? "record"}
            </>
          )}
          {leader.target && !hideTargetChip && (
            <>
              {VERB_TARGET_JOINERS[leader.verb] ?? " "}
              <SidebarChip
                value={leader.target}
                impliedKind={VERB_IMPLIED_KIND[leader.verb]}
              />
            </>
          )}
        </span>
      ) : (
        <RowSummary row={leader} hideTargetChip={hideTargetChip} />
      )}
    </>
  );

  return (
    <div
      data-activity-row
      style={{
        padding: "7px 0",
        fontSize: "var(--fp-t-body)",
        color: "var(--fp-ink-2)",
        lineHeight: 1.55,
      }}
    >
      {/* No circled glyph: a ring at identical position on every row is
          the per-row texture rule 31 bans (CD Aug-31 — it predated the
          rule). The bold actor name leads every row; the feed's only
          structure is its day and run headers. */}
      <div className="min-w-0">
        <div className="flex items-baseline min-w-0">
          <span className={oneLine ? "min-w-0 truncate" : "min-w-0"}>
            {sentence}
          </span>
          {reasoning && (
            // A real BUTTON beside the sentence, never a role on the
            // sentence itself: ARIA makes a button's children
            // presentational, which would strip the entity links inside
            // the sentence from assistive tech, and a row-wide click
            // target makes selecting the prose toggle the row — the exact
            // can't-select-or-quote complaint the disclosure exists to
            // fix.
            <WhyButton
              open={reasoningOpen}
              actorName={actorName}
              onToggle={() => setReasoningOpen((v) => !v)}
            />
          )}
          {entry.kind === "repeat" && (
            // "· N times" is the row's ONE disclosure control (CD Aug-31):
            // it opens the occurrence list. A collapsed row never carries
            // a row-level Why beside it — the row holds several events and
            // therefore possibly several reasonings; each occurrence
            // discloses its own.
            <button
              type="button"
              aria-expanded={occurrencesOpen}
              // The visible "N times" opens the accessible name (WCAG
              // 2.5.3, same rule as the Why button beside it) and the
              // name never flips — aria-expanded carries the state.
              aria-label={`${repeatTotal} times — occurrences`}
              onClick={() => setOccurrencesOpen((v) => !v)}
              className={`flex-none inline-flex items-center ml-1.5 align-baseline rounded px-1 py-0.5 cursor-pointer whitespace-nowrap focus-visible:outline focus-visible:outline-1 focus-visible:outline-gray-400 ${
                occurrencesOpen
                  ? "underline text-[var(--fp-ink-2)]"
                  : "text-[var(--fp-ink-4)] hover:underline hover:text-[var(--fp-ink-2)]"
              }`}
              style={{ fontSize: "var(--fp-t-meta)" }}
            >
              · {repeatTotal} times
            </button>
          )}
          {/* A date is a fact: the clock reads at the data level in the
              fixed right column, tabular so a column of times aligns down
              the whole panel — one time column for singles and run rows
              alike (CD Aug-31). The full moment stays one hover away. */}
          <ClockColumn labels={occurred} />
        </div>
        {reasoning && reasoningOpen && (
          <ReasoningBlock>{reasoning}</ReasoningBlock>
        )}
        {entry.kind === "repeat" && occurrencesOpen && (
          // The folded moments, newest first — the list LEADS with the
          // same timestamp the collapsed row is stamped with, so the
          // relationship is obvious rather than reading as a fourth event
          // (CD Aug-31). Each occurrence carries its own Why where it has
          // reasoning.
          <div
            className="mt-1.5 pl-3"
            style={{ borderLeft: "2px solid var(--fp-border)" }}
          >
            {[entry.leader, ...entry.members].map((occ) => (
              <OccurrenceRow
                key={occ.keyset_id}
                occ={occ}
                viewerZone={viewerZone}
                open={openOccurrenceWhy === occ.keyset_id}
                onToggle={() =>
                  setOpenOccurrenceWhy((current) =>
                    current === occ.keyset_id ? null : occ.keyset_id
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ActorBadge: React.FC<{ row: ActivityLogRow }> = ({ row }) => {
  // The actor leads the sentence in primary ink (design round 5); no glyph
  // (#11426, register R31) — an event feed's actor field has AI as a
  // normal, frequent value, so a mark on every such row is texture by
  // type. The fact is stated in words.
  const name = row.is_finpilot_ai
    ? "Finpilot AI"
    : (row.actor_display ?? row.user_id ?? "System");
  return (
    <span className="font-medium" style={{ color: "var(--fp-ink)" }}>
      {name}
    </span>
  );
};

const BulkOthersSuffix: React.FC<{ row: ActivityLogRow }> = ({ row }) => {
  const total = row.bulk_target_count;
  if (!total || total <= 1) return null;
  const others = total - 1;
  return (
    <span className="text-gray-500">
      {" and "}
      <span className="font-medium">{others}</span>
      {others === 1 ? " other" : " others"}
    </span>
  );
};

const RowSummary: React.FC<{
  row: ActivityLogRow;
  hideTargetChip?: boolean;
  // A document's own history: "from <the document>" would name the page.
  hideSourceDocumentId?: string;
}> = ({ row, hideTargetChip = false, hideSourceDocumentId }) => {
  // The org's document_type vocabulary, for rendering the raw type_keys a
  // move-document row records as display names (#11009). Shared react-query
  // cache, so per-row calls dedupe to one fetch.
  const { data: documentConfig } = useDocumentConfig();
  const documentTypeLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const t of documentConfig?.document_types ?? []) {
      labels[t.type_key] = t.display_name;
    }
    return labels;
  }, [documentConfig]);
  const factChanges =
    row.verb === "update-fact" ? extractFactChanges(row) : null;
  if (factChanges && factChanges.length > 0) {
    return (
      <span>
        updated{" "}
        {factChanges.map((change, i) => (
          <React.Fragment key={change.field}>
            {i > 0 && ", "}
            <span className="font-medium">{change.field}</span>
            {change.displayValue !== null && (
              <>
                {" to "}
                <span className="font-medium">{change.displayValue}</span>
              </>
            )}
          </React.Fragment>
        ))}
        {row.target && !hideTargetChip && (
          <>
            {" for "}
            <SidebarChip
              value={row.target}
              impliedKind={VERB_IMPLIED_KIND[row.verb]}
            />
            <BulkOthersSuffix row={row} />
          </>
        )}
      </span>
    );
  }

  if (row.verb === "link-documents") {
    const count = countBlobIds(row);
    return (
      <span>
        uploaded {count} {count === 1 ? "document" : "documents"}
        {row.target && !hideTargetChip && (
          <>
            {" to "}
            <SidebarChip
              value={row.target}
              impliedKind={VERB_IMPLIED_KIND[row.verb]}
            />
          </>
        )}
      </span>
    );
  }

  if (row.verb === "unlink-documents") {
    const count = countUnlinkBlobIds(row);
    return (
      <span>
        unlinked {count} {count === 1 ? "document" : "documents"}
        {row.target && !hideTargetChip && (
          <>
            {" from "}
            <SidebarChip
              value={row.target}
              impliedKind={VERB_IMPLIED_KIND[row.verb]}
            />
          </>
        )}
      </span>
    );
  }

  if (row.verb === "unlink-document") {
    return (
      <span>
        unlinked{" "}
        {row.source ? <SidebarChip value={row.source} /> : "a document"}
        {row.target && !hideTargetChip && (
          <>
            {" from "}
            <SidebarChip
              value={row.target}
              impliedKind={VERB_IMPLIED_KIND[row.verb]}
            />
          </>
        )}
      </span>
    );
  }

  if (row.verb === "move-document") {
    const newType = extractDocumentType(row, documentTypeLabels);
    return (
      <span>
        moved
        {row.target && !hideTargetChip && (
          <>
            {" "}
            <SidebarChip
              value={row.target}
              impliedKind={VERB_IMPLIED_KIND[row.verb]}
            />
          </>
        )}
        {newType && (
          <>
            {" to "}
            <span className="font-medium">{newType}</span>
          </>
        )}
      </span>
    );
  }

  if (row.verb === "set-manager") {
    const managerDisplay = extractManagerDisplay(row);
    return (
      <span>
        set the manager on
        {row.target && !hideTargetChip && (
          <>
            {" "}
            <SidebarChip
              value={row.target}
              impliedKind={VERB_IMPLIED_KIND[row.verb]}
            />
          </>
        )}
        {managerDisplay && (
          <>
            {" to "}
            <SidebarChip value={managerDisplay} />
          </>
        )}
      </span>
    );
  }

  if (row.verb === "update-interaction") {
    const changedFields = extractChangedFields(row);
    return (
      <span>
        updated
        {changedFields && changedFields.length > 0 && (
          <>
            {" "}
            <span className="font-medium">{changedFields.join(", ")}</span>
          </>
        )}
        {row.target && !hideTargetChip && (
          <>
            {" on "}
            <SidebarChip
              value={row.target}
              impliedKind={VERB_IMPLIED_KIND[row.verb]}
            />
          </>
        )}
      </span>
    );
  }

  if (row.verb === "link-document") {
    const filename = extractFilename(row);
    return (
      <span>
        uploaded{" "}
        {filename ? (
          <span className="font-medium">{filename}</span>
        ) : (
          "a document"
        )}
        {row.target && !hideTargetChip && (
          <>
            {" to "}
            <SidebarChip
              value={row.target}
              impliedKind={VERB_IMPLIED_KIND[row.verb]}
            />
          </>
        )}
      </span>
    );
  }

  if (row.verb === "rename-entity") {
    const newName = extractRenamedName(row);
    return (
      <span>
        renamed
        {newName && (
          <>
            {" to "}
            <span className="font-medium">{newName}</span>
          </>
        )}
      </span>
    );
  }

  const verb = formatVerb(row.verb);
  const skipSource =
    row.verb === "create-interaction" ||
    (hideSourceDocumentId !== undefined &&
      row.source?.kind === "document" &&
      row.source.id === hideSourceDocumentId);
  // On a record's own Activity tab the target chip is hidden — it names the
  // page — but a bare verb still needs an object: "registered ×5" states an
  // actor, a verb, and a number, and no thing (design round 7). "this
  // <kind>" says it without repeating the name.
  const selfObject =
    hideTargetChip && row.target
      ? (entityKindMap[row.target.kind as EntityKind]?.label.toLowerCase() ??
        "record")
      : null;
  return (
    <span>
      {verb}
      {selfObject && <> this {selfObject}</>}
      {row.target && !hideTargetChip && (
        <>
          {VERB_TARGET_JOINERS[row.verb] ?? " "}
          <SidebarChip
            value={row.target}
            impliedKind={VERB_IMPLIED_KIND[row.verb]}
          />
          <BulkOthersSuffix row={row} />
        </>
      )}
      {row.source && !skipSource && (
        <>
          {" from "}
          <SidebarChip value={row.source} />
        </>
      )}
    </span>
  );
};

// ─── A document's own history (#13433) ───────────────────────────
//
// The document is the page, so its sentences never name it: a change to it
// reads "changed Type to Pitch Deck", a link made from its side "linked
// Alyeska Fund I as Vehicle", and a link made from a record's side (logged
// under the record, with the document as its source) "linked to Alyeska".

// The document's own fields, in the order and under the labels the Details
// panel shows them. The keys are the ones the document writers record in
// details_before / details_after; a field no writer records yet (Manager,
// Fund Name, Strategy, Publisher) gets its label here when one does.
const DOCUMENT_FIELD_LABELS: ReadonlyArray<readonly [string, string]> = [
  ["document_title", "Title"],
  ["document_types", "Type"],
  ["document_date", "Document Date"],
];

const LINK_VERBS = new Set(["link-document", "link-documents"]);
const UNLINK_VERBS = new Set(["unlink-document", "unlink-documents"]);

function detailsObject(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

// Equal as stored, with an absent key reading as null.
function sameStored(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// A field's new value as the Details panel shows it, or null when the write
// left it empty. A type shows its display name, never its key; a document
// date is a calendar day and reads as one, never through a time zone.
function documentFieldValue(
  key: string,
  raw: unknown,
  typeLabels: Record<string, string>
): string | null {
  if (raw === null || raw === undefined || raw === "") return null;
  if (key === "document_types") {
    const keys = (Array.isArray(raw) ? raw : [raw]).filter(
      (t): t is string => typeof t === "string" && t !== ""
    );
    return keys.length
      ? keys.map((t) => typeLabels[t] ?? humanizeEnumKey(t)).join(", ")
      : null;
  }
  if (key === "document_date") {
    return typeof raw === "string" ? formatCalendarDay(raw, "medium") : null;
  }
  if (Array.isArray(raw)) {
    const parts = raw.filter(
      (v): v is string => typeof v === "string" && v !== ""
    );
    return parts.length ? parts.join(", ") : null;
  }
  return formatScalar(raw);
}

// The custom columns a row wrote: the org's own column names, and each new
// value as the feed renders fact values elsewhere. A column whose stored
// value the write did not change is left out.
function documentColumnChanges(
  before: Record<string, unknown> | null,
  after: Record<string, unknown>
): FactChange[] {
  const payload = detailsObject(after.payload);
  const facts = detailsObject(after.facts) ?? detailsObject(payload?.facts);
  if (!facts) return [];
  const priorFacts = detailsObject(before?.facts);
  return Object.entries(facts)
    .filter(
      ([field, value]) => !priorFacts || !sameStored(priorFacts[field], value)
    )
    .map(([field, value]) => ({ field, displayValue: formatFactValue(value) }));
}

// "linked A, B as Vehicle" — one clause per kind, in the order the records
// arrived.
function linkClauses(
  verb: "linked" | "unlinked",
  chips: PolymorphicCellValue[] | null | undefined
): React.ReactNode[] {
  if (!chips || chips.length === 0) return [];
  const byKind = new Map<string, PolymorphicCellValue[]>();
  for (const chip of chips) {
    const group = byKind.get(chip.kind);
    if (group) group.push(chip);
    else byKind.set(chip.kind, [chip]);
  }
  return [...byKind].map(([kind, group]) => (
    <React.Fragment key={`${verb}-${kind}`}>
      {verb}{" "}
      {group.map((chip, i) => (
        <React.Fragment key={chip.id}>
          {i > 0 && ", "}
          <SidebarChip value={chip} hideGlyph />
        </React.Fragment>
      ))}
      {" as "}
      {entityKindMap[kind as EntityKind]?.label ?? "Record"}
    </React.Fragment>
  ));
}

// The clauses of a row that targets the document, newest state first:
// renamed, moved, the fields it set, the fields it cleared, the records it
// linked and unlinked. Empty when the row records nothing the reader would
// recognise as a change.
function documentChangeClauses(
  row: ActivityLogRow,
  typeLabels: Record<string, string>
): React.ReactNode[] {
  const before = detailsObject(row.details_before);
  const after = detailsObject(row.details_after);
  const clauses: React.ReactNode[] = [];
  if (after) {
    const changedKey = (key: string): boolean =>
      key in after && !sameStored(before?.[key], after[key]);
    if (changedKey("filename") && typeof after.filename === "string") {
      clauses.push(
        <React.Fragment key="renamed">
          renamed to <span className="font-medium">{after.filename}</span>
        </React.Fragment>
      );
    }
    if (changedKey("filesystem_path") && Array.isArray(after.filesystem_path)) {
      const folders = after.filesystem_path.filter(
        (f): f is string => typeof f === "string"
      );
      clauses.push(
        <React.Fragment key="moved">
          moved
          {folders.length > 0 && (
            <>
              {" to "}
              <span className="font-medium">{folders.join(" / ")}</span>
            </>
          )}
        </React.Fragment>
      );
    }
    const set: FactChange[] = [];
    const cleared: string[] = [];
    for (const [key, label] of DOCUMENT_FIELD_LABELS) {
      if (!changedKey(key)) continue;
      const value = documentFieldValue(key, after[key], typeLabels);
      if (value === null) cleared.push(label);
      else set.push({ field: label, displayValue: value });
    }
    for (const change of documentColumnChanges(before, after)) {
      if (change.displayValue === null) cleared.push(change.field);
      else set.push(change);
    }
    if (set.length > 0) {
      clauses.push(
        <React.Fragment key="changed">
          changed{" "}
          {set.map((change, i) => (
            <React.Fragment key={change.field}>
              {i > 0 && ", "}
              <span className="font-medium">{change.field}</span>
              {" to "}
              <span className="font-medium">{change.displayValue}</span>
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    }
    if (cleared.length > 0) {
      clauses.push(
        <React.Fragment key="cleared">
          cleared{" "}
          {cleared.map((field, i) => (
            <React.Fragment key={field}>
              {i > 0 && ", "}
              <span className="font-medium">{field}</span>
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    }
  }
  clauses.push(...linkClauses("linked", row.linked));
  clauses.push(...linkClauses("unlinked", row.unlinked));
  return clauses;
}

const DocumentRowSummary: React.FC<{
  row: ActivityLogRow;
  documentId: string;
}> = ({ row, documentId }) => {
  const { data: documentConfig } = useDocumentConfig();
  const typeLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const t of documentConfig?.document_types ?? []) {
      labels[t.type_key] = t.display_name;
    }
    return labels;
  }, [documentConfig]);

  const targetIsDocument =
    row.target?.kind === "document" && row.target.id === documentId;
  const sourceIsDocument =
    row.source?.kind === "document" && row.source.id === documentId;

  // A link made from the record's side: the record is the row's target and
  // this document its source.
  if (!targetIsDocument && sourceIsDocument && row.target) {
    if (LINK_VERBS.has(row.verb) || UNLINK_VERBS.has(row.verb)) {
      const linking = LINK_VERBS.has(row.verb);
      return (
        <span>
          {linking ? "linked to " : "unlinked from "}
          <SidebarChip value={row.target} hideGlyph />
        </span>
      );
    }
  }

  if (targetIsDocument) {
    const clauses = documentChangeClauses(row, typeLabels);
    return (
      <span>
        {clauses.length === 0
          ? "updated this document"
          : clauses.map((clause, i) => (
              <React.Fragment key={i}>
                {i > 0 && ", "}
                {clause}
              </React.Fragment>
            ))}
      </span>
    );
  }

  // Anything else the document took part in (a record registered from it,
  // say) reads as the feed reads it everywhere, less the document's name.
  return (
    <RowSummary
      row={row}
      hideTargetChip={false}
      hideSourceDocumentId={documentId}
    />
  );
};

function countBlobIds(row: ActivityLogRow): number {
  const d = row.details_after as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return 0;
  const ids = d.blob_ids;
  return Array.isArray(ids) ? ids.length : 0;
}

function countUnlinkBlobIds(row: ActivityLogRow): number {
  const d = row.details_after as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return 0;
  const ids = d.unlinked_blob_ids;
  return Array.isArray(ids) ? ids.length : 0;
}

// The new Subtype a move-document row landed the document in, recorded by the
// writer under details_after.document_types. Null when absent so the summary
// degrades to a bare "moved <doc>" instead of a dangling "moved … to".
//
// The recorded values are raw type_keys; render the org's display names,
// humanizing keys the org hasn't configured so snake_case never leaks
// (#11009). Reads the legacy scalar `document_type` too: rows written before
// a document could carry several types are still in the feed, and they'd
// otherwise render as a bare "moved" forever.
function extractDocumentType(
  row: ActivityLogRow,
  labels: Record<string, string>
): string | null {
  const display = (key: string): string => labels[key] ?? humanizeEnumKey(key);
  const d = row.details_after as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return null;
  const types = d.document_types;
  if (Array.isArray(types)) {
    const real = types.filter((t): t is string => typeof t === "string" && !!t);
    return real.length ? real.map(display).join(", ") : null;
  }
  return typeof d.document_type === "string" && d.document_type
    ? display(d.document_type)
    : null;
}

function extractManagerDisplay(
  row: ActivityLogRow
): PolymorphicCellValue | null {
  const d = row.details_after as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return null;
  const display = d.manager_display as Record<string, unknown> | undefined;
  if (!display || typeof display !== "object") return null;
  const publicId = display.public_id;
  const name = display.name;
  if (typeof publicId !== "string" || typeof name !== "string") return null;
  const kindRaw = display.kind;
  const kind = typeof kindRaw === "string" ? kindRaw : "manager";
  return { kind, id: publicId, display: name };
}

function extractChangedFields(row: ActivityLogRow): string[] | null {
  const d = row.details_after as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return null;
  const fields = d.changed_fields;
  if (!Array.isArray(fields)) return null;
  return fields.filter((f): f is string => typeof f === "string");
}

function extractFilename(row: ActivityLogRow): string | null {
  const d = row.details_after as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return null;
  return typeof d.filename === "string" ? d.filename : null;
}

// The new name for a rename-entity row, recorded by the writer under
// details_after.payload.name (#9784). Null when absent so the summary
// degrades to a bare "renamed" instead of a dangling "renamed to".
function extractRenamedName(row: ActivityLogRow): string | null {
  const d = row.details_after as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return null;
  const payload = d.payload as Record<string, unknown> | undefined;
  if (!payload || typeof payload !== "object") return null;
  const name = payload.name;
  return typeof name === "string" && name ? name : null;
}

// True when a generic entity-update row carries no describable change —
// no rename, no fact edit, no alias change. These are no-op saves that
// would otherwise render as a bare "updated" with no object (#9784).
// Scoped to the entity-update writer's shape (a `payload` wrapper) so
// legacy update rows of other shapes are never hidden.
function isContentlessUpdate(row: ActivityLogRow): boolean {
  if (row.verb !== "update" && row.verb !== "update-entity") return false;
  const d = row.details_after as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return false;
  if (d.renamed_from) return false;
  const payload = d.payload as Record<string, unknown> | undefined;
  if (!payload || typeof payload !== "object") return false;
  const facts = payload.facts;
  if (facts && typeof facts === "object" && Object.keys(facts).length > 0) {
    return false;
  }
  const hasItems = (key: string): boolean => {
    const v = payload[key];
    return Array.isArray(v) && v.length > 0;
  };
  if (hasItems("aliases") || hasItems("deleted_aliases")) return false;
  return true;
}

interface FactChange {
  field: string;
  displayValue: string | null;
}

// True when an update-fact row only clears fields (every fact change
// resolves to a null displayValue) — rendered as bare "updated Foo"
// with no value, which is noise for users scanning the feed.
function isFieldClear(row: ActivityLogRow): boolean {
  if (row.verb !== "update-fact") return false;
  const changes = extractFactChanges(row);
  if (!changes || changes.length === 0) return false;
  return changes.every((c) => c.displayValue === null);
}

function extractFactChanges(row: ActivityLogRow): FactChange[] | null {
  const details = row.details_after as Record<string, unknown> | null;
  if (!details || typeof details !== "object") return null;
  let facts = details.facts;
  if (!facts || typeof facts !== "object") {
    const payload = details.payload as Record<string, unknown> | undefined;
    if (payload && typeof payload === "object") {
      facts = payload.facts;
    }
  }
  if (!facts || typeof facts !== "object") return null;
  const entries = Object.entries(facts as Record<string, unknown>);
  if (entries.length === 0) return null;
  return entries.map(([field, raw]) => ({
    field,
    displayValue: formatFactValue(raw),
  }));
}

function formatFactValue(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const value = r.value as Record<string, unknown> | null | undefined;
  if (value && typeof value === "object") {
    const scalar = value.scalar;
    if (scalar !== undefined && scalar !== null) {
      return formatScalar(scalar);
    }
    const entityName = extractEntityDisplayName(value);
    if (entityName) return entityName;
  }
  const values = r.values;
  if (Array.isArray(values) && values.length > 0) {
    const parts = values
      .map((v) => {
        if (v && typeof v === "object") {
          const o = v as Record<string, unknown>;
          const s = o.scalar;
          if (s !== undefined && s !== null) return formatScalar(s);
          const name = extractEntityDisplayName(o);
          if (name) return name;
        }
        return null;
      })
      .filter((s): s is string => s !== null);
    if (parts.length > 0) return parts.join(", ");
  }
  return null;
}

function extractEntityDisplayName(
  entry: Record<string, unknown>
): string | null {
  const display = entry.entity_display;
  if (
    display &&
    typeof display === "object" &&
    typeof (display as Record<string, unknown>).name === "string"
  ) {
    return (display as Record<string, unknown>).name as string;
  }
  return null;
}

function formatScalar(s: unknown): string {
  if (typeof s === "boolean") return s ? "Yes" : "No";
  if (typeof s === "number") return s.toLocaleString();
  if (typeof s === "string") return s;
  if (s && typeof s === "object") {
    const obj = s as Record<string, unknown>;
    if (typeof obj.formatted === "string" && obj.formatted) {
      return obj.formatted;
    }
    if (Array.isArray(s)) {
      return s.map((v) => formatScalar(v)).join(" → ");
    }
  }
  return String(s);
}

const GroupedSummary: React.FC<{
  leader: ActivityLogRow;
  members: ActivityLogRow[];
  fromInteraction?: PolymorphicCellValue;
  hideTargetChip?: boolean;
  // On a document's own history the document is the page: "from <it>"
  // would name it.
  hideFromDocumentId?: string;
}> = ({
  leader,
  members,
  fromInteraction,
  hideTargetChip = false,
  hideFromDocumentId,
}) => {
  const all = [leader, ...members];
  const named = all.filter((r) => r.target);
  const candidateFrom = fromInteraction ?? leader.source;
  const fromChip =
    hideFromDocumentId &&
    candidateFrom?.kind === "document" &&
    candidateFrom.id === hideFromDocumentId
      ? null
      : candidateFrom;
  return (
    <span>
      registered
      {!hideTargetChip && (
        <>
          {" "}
          {named.length <= 2 ? (
            named.map((r, i) => (
              <React.Fragment key={r.entity_id}>
                {i > 0 && " and "}
                <SidebarChip value={r.target} />
              </React.Fragment>
            ))
          ) : (
            <>
              <SidebarChip value={named[0].target} />
              {", "}
              <SidebarChip value={named[1].target} />
              {`, and ${named.length - 2} ${named.length - 2 === 1 ? "other" : "others"}`}
            </>
          )}
        </>
      )}
      {fromChip && (
        <>
          {" from "}
          <SidebarChip value={fromChip} />
        </>
      )}
    </span>
  );
};

// ─── Clustering ──────────────────────────────────────────────────

function clusterAiBatches(rows: DatedActivityLogRow[]): ClusteredEntry[] {
  const out: ClusteredEntry[] = [];
  let i = 0;
  while (i < rows.length) {
    const leader = rows[i];
    // A run the backend folded (#11876) is its own entry: its members ride
    // on the leader and are never re-clustered or re-collapsed here.
    if (leader.run_event_count !== null && leader.run_object_count !== null) {
      out.push({
        kind: "run",
        leader,
        members: (leader.run_members ?? []).filter(hasOccurrence),
        eventCount: leader.run_event_count,
        objectCount: leader.run_object_count,
      });
      i += 1;
      continue;
    }
    const canCluster =
      leader.verb === "create-entity" &&
      leader.is_finpilot_ai &&
      leader.source?.kind === "document" &&
      !!leader.source.id;
    if (!canCluster) {
      out.push({ kind: "row", leader });
      i += 1;
      continue;
    }
    const docId = leader.source!.id;
    const members: DatedActivityLogRow[] = [];
    let j = i + 1;
    while (j < rows.length) {
      const next = rows[j];
      if (
        next.verb === "create-entity" &&
        next.is_finpilot_ai &&
        next.source?.kind === "document" &&
        next.source.id === docId
      ) {
        members.push(next);
        j += 1;
      } else {
        break;
      }
    }
    if (members.length === 0) {
      out.push({ kind: "row", leader });
    } else {
      out.push({ kind: "group", leader, members });
    }
    i = j;
  }
  return out;
}

function attachInteractionSibling(entries: ClusteredEntry[]): ClusteredEntry[] {
  // Recorded interactions may have been folded into a run (#11876), whose
  // leader and shipped members are candidates too.
  const candidates: DatedActivityLogRow[] = entries.flatMap((e) =>
    e.kind === "row"
      ? [e.leader]
      : e.kind === "run"
        ? [e.leader, ...e.members]
        : []
  );
  return entries.map((entry) => {
    if (entry.kind !== "group") return entry;
    const sourceId = entry.leader.source?.id;
    if (!sourceId) return entry;
    const sibling = candidates.find(
      (row) =>
        row.verb === "create-interaction" &&
        row.is_finpilot_ai &&
        row.source?.kind === "document" &&
        row.source.id === sourceId &&
        !!row.target
    );
    if (!sibling || !sibling.target) return entry;
    return { ...entry, fromInteraction: sibling.target };
  });
}

// ─── Formatting ──────────────────────────────────────────────────

const VERB_LABELS: Record<string, string> = {
  create: "created",
  "create-entity": "registered",
  // Named object: suppression may remove redundancy, never distinction —
  // a snapshot creation and the record's own must not read alike (#11426).
  "create-snapshot": "registered a holdings snapshot",
  "create-interaction": "recorded",
  delete: "deleted",
  update: "updated",
  "update-entity": "updated",
  "update-interaction": "updated",
  "update-document": "updated",
  "update-fact": "updated",
  "update-schema": "updated the schema for",
  "update-columns": "updated columns on",
  "update-document-groups": "updated document groups",
  "update-interaction-types": "updated interaction types",
  "update-returns": "updated returns for",
  "update-snapshot": "updated a snapshot for",
  "override-return": "overrode a return for",
  "delete-performance-series": "deleted performance series for",
  "set-manager": "set the manager on",
  "link-document": "linked a document to",
  "link-documents": "uploaded documents to",
  "rename-entity": "renamed",
};

// A verb whose label ends in a noun joins its target with a preposition —
// "registered a holdings snapshot Harbourview Endowment Pool" ran two noun
// phrases together (design round 6).
const VERB_TARGET_JOINERS: Record<string, string> = {
  "create-snapshot": " for ",
};

function formatVerb(verb: string): string {
  return VERB_LABELS[verb] ?? verb.replace(/-/g, " ");
}

// ─── Inline chip renderer ────────────────────────────────────────

// Verbs that already name their object's kind: the chip's kind glyph
// would restate the sentence, so it stands down (CD Aug-31 — "keep the
// small type glyph only where the verb doesn't already imply the type").
const VERB_IMPLIED_KIND: Record<string, string> = {
  "create-interaction": "interaction",
  "update-interaction": "interaction",
};

const SidebarChip: React.FC<{
  value: PolymorphicCellValue | null;
  impliedKind?: string;
  // Run object rows always drop the glyph — the header owns the verb.
  hideGlyph?: boolean;
}> = ({ value, impliedKind, hideGlyph = false }) => {
  if (!value) return <span className="text-gray-400">—</span>;

  // Every kind the metadata map knows — interactions included — wears the
  // system's doorway (InlineEntityCard), so the feed stays travelable and
  // a newly added kind never renders as inert text. The dress is QUIET
  // here (#11426 Aug-31, CD ruling superseding round-6's at-rest
  // underline): ink-2 with the underline arriving on hover, so a stack of
  // references reads as sentences rather than boxes.
  if (value.kind in entityKindMap) {
    const entity: ShallowEntity = {
      id: value.id,
      kind: value.kind as EntityKind,
      name: value.display ?? value.id,
    };
    return (
      <InlineEntityCard
        entity={entity}
        quiet
        hideIcon={hideGlyph || value.kind === impliedKind}
      />
    );
  }

  if (value.kind === "document") {
    return <span className="text-gray-700">{value.display ?? value.id}</span>;
  }

  return (
    <span className="text-gray-500 italic">{value.display || "Unknown"}</span>
  );
};

export default ActivityFeed;
